import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const api = axios.create({
  baseURL: API_URL,
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth
export const authApi = {
  login: (data) => api.post('/auth/login', data),
  forgotPassword: (email) => api.post(`/auth/forgot-password?email=${email}`),
  getMe: () => api.get('/auth/me'),
};

// Users
export const usersApi = {
  getAll: (params) => api.get('/users', { params }),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.patch(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
  getBySpecialties: (specialtyIds, roles) => {
    const params = { specialty_ids: specialtyIds.join(',') };
    if (roles) params.roles = roles.join(',');
    return api.get('/users/by-specialties', { params });
  },
};

// Admin Impersonation
export const impersonationApi = {
  impersonate: (userId) => api.post(`/admin/impersonate/${userId}`),
  stopImpersonate: () => api.post('/admin/stop-impersonate'),
  getStatus: () => api.get('/auth/impersonation-status'),
};

// Specialties
export const specialtiesApi = {
  getAll: () => api.get('/specialties'),
  create: (data) => api.post('/specialties', data),
  update: (id, data) => api.patch(`/specialties/${id}`, data),
  delete: (id) => api.delete(`/specialties/${id}`),
};

// Company Accounts
export const companyAccountsApi = {
  getAll: () => api.get('/company-accounts'),
  getById: (id) => api.get(`/company-accounts/${id}`),
  create: (data) => api.post('/company-accounts', data),
  update: (id, data) => api.patch(`/company-accounts/${id}`, data),
  delete: (id) => api.delete(`/company-accounts/${id}`),
  // Admin password management
  resetPassword: (id) => api.post(`/admin/companies/${id}/reset-password`),
  setPassword: (id, password) => api.post(`/admin/companies/${id}/set-password`, { password }),
  // Admin approval workflow
  approve: (id) => api.post(`/admin/companies/${id}/approve`),
  reject: (id, reason) => api.post(`/admin/companies/${id}/reject`, { reason }),
  // Admin impersonation (auto-provisions owner if missing)
  impersonate: (id) => api.post(`/admin/companies/${id}/impersonate`),
};

// Pending registrations & admin password ops on users
export const adminRegistrationsApi = {
  getPending: () => api.get('/admin/pending-registrations'),
  approveUser: (id) => api.post(`/admin/users/${id}/approve`),
  rejectUser: (id, reason) => api.post(`/admin/users/${id}/reject`, { reason }),
  resetUserPassword: (id) => api.post(`/admin/users/${id}/reset-password`),
  setUserPassword: (id, password) => api.post(`/admin/users/${id}/set-password`, { password }),
};

// Company-owned reps (used by Company Account owner login)
export const companyRepsApi = {
  list: () => api.get('/company/reps'),
  invite: (email) => api.post('/company/reps/invite', { email }),
  remove: (id) => api.delete(`/company/reps/${id}`),
};

// Request Type Settings
export const requestTypeSettingsApi = {
  getAll: () => api.get('/request-type-settings'),
  getByType: (type) => api.get(`/request-type-settings/${type}`),
  update: (type, data) => api.patch(`/request-type-settings/${type}`, data),
  getUploadDocTypes: (type) => api.get(`/request-type-settings/${type}/upload-doc-types`),
  updateUploadDocTypes: (type, docTypes) => api.patch(`/request-type-settings/${type}/upload-doc-types`, { doc_types: docTypes }),
  // Overview tab layout
  getOverviewLayout: (type) => api.get(`/request-type-settings/${type}/overview-layout`),
  updateOverviewLayout: (type, data) => api.patch(`/request-type-settings/${type}/overview-layout`, data),
  // Document bundle tool
  getBundleToolConfig: (type) => api.get(`/request-type-settings/${type}/bundle-tool`),
  updateBundleToolConfig: (type, data) => api.patch(`/request-type-settings/${type}/bundle-tool`, data),
  // Signature rounds (Phase 3)
  getSignatureRoundsConfig: (type) => api.get(`/request-type-settings/${type}/signature-rounds`),
  updateSignatureRoundsConfig: (type, data) => api.patch(`/request-type-settings/${type}/signature-rounds`, data),
  // Role visibility matrix (configurable platform — step 1)
  getRoleVisibility: (type) => api.get(`/request-type-settings/${type}/role-visibility`),
  updateRoleVisibility: (type, roles) => api.patch(`/request-type-settings/${type}/role-visibility`, { roles }),
  previewRoleVisibility: (type, params) => api.get(`/request-type-settings/${type}/role-visibility/preview`, { params }),
  // iter167 Issue 35 — Per-study instance cap (Once / Unlimited / Fixed)
  getPerStudyLimit: (type) => api.get(`/request-type-settings/${type}/per-study-limit`),
  updatePerStudyLimit: (type, data) => api.patch(`/request-type-settings/${type}/per-study-limit`, data),
  // Form Configuration
  getFormConfig: (type, path) => api.get(`/request-type-settings/${type}/form-config${path ? `?path=${path}` : ''}`),
  updateFormConfig: (type, data, path) => api.patch(`/request-type-settings/${type}/form-config${path ? `?path=${path}` : ''}`, data),
  // Custom Fields
  getCustomFields: (type, path) => api.get(`/request-type-settings/${type}/custom-fields${path ? `?path=${path}` : ''}`),
  addCustomField: (type, field, path) => api.post(`/request-type-settings/${type}/custom-fields${path ? `?path=${path}` : ''}`, field),
  updateCustomField: (type, fieldId, field, path) => api.patch(`/request-type-settings/${type}/custom-fields/${fieldId}${path ? `?path=${path}` : ''}`, field),
  deleteCustomField: (type, fieldId, path) => api.delete(`/request-type-settings/${type}/custom-fields/${fieldId}${path ? `?path=${path}` : ''}`),
  updateFieldOrder: (type, fieldOrder, path) => api.patch(`/request-type-settings/${type}/custom-fields-order${path ? `?path=${path}` : ''}`, { field_order: fieldOrder }),
  updateUnifiedFieldOrder: (type, order, path) => api.patch(`/request-type-settings/${type}/unified-field-order${path ? `?path=${path}` : ''}`, { unified_field_order: order }),
  uploadTemplateFile: (type, fieldId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/request-type-settings/${type}/custom-fields/${fieldId}/upload-template`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  downloadTemplateFile: (fileId) => api.get(`/template-files/${fileId}/download`, { responseType: 'blob' }),
};

// Requests
export const requestsApi = {
  getAll: (params) => api.get('/requests', { params }),
  getById: (id) => api.get(`/requests/${id}`),
  create: (data, config) => api.post('/requests', data, config),
  submit: (id) => api.post(`/requests/${id}/submit`),
  routeToConsultants: (id, data) => api.post(`/requests/${id}/route-to-consultants`, data || {}),
  routeSingleConsultant: (id, consultantId) => api.post(`/requests/${id}/route-single-consultant?consultant_id=${consultantId}`),
  getPendingConsultantInvites: (id) => api.get(`/requests/${id}/pending-consultant-invites`),
  getEligibleConsultants: (id) => api.get(`/requests/${id}/eligible-consultants`),
  resubmit: (id) => api.post(`/requests/${id}/resubmit`),
  close: (id, status, body = null) => api.post(`/requests/${id}/close?status=${status}`, body || {}),
  getEligibleFeaPiCandidates: (id) => api.get(`/requests/${id}/eligible-fea-pi-candidates`),
  getStudyFeaPiSuggestions: (studyId) => api.get(`/studies/${studyId}/fea-pi-suggestions`),
  withdraw: (id) => api.post(`/requests/${id}/withdraw`),
  adminDelete: (id) => api.delete(`/admin/requests/${id}`),
  updateSpecialties: (id, data) => api.patch(`/requests/${id}/specialties`, data),
  updateCompany: (id, data) => api.patch(`/requests/${id}/company`, data),
  getPendingHandover: (id) => api.get(`/requests/${id}/pending-handover`),
  createHandover: (id, data) => api.post(`/requests/${id}/handover`, data),
  getHandoverStatus: (id) => api.get(`/requests/${id}/handover-status`),
  getHandoverHistory: (id) => api.get(`/requests/${id}/handover-history`),
  checkDocThreadAccess: (id, docType) => api.get(`/requests/${id}/doc-thread-check?doc_type=${encodeURIComponent(docType)}`),
  createAccessRequest: (id, data) => api.post(`/requests/${id}/access-request`, data),
  // iter167 Issue 12 — toggle viewer-only (read-only) access for any
  // assignee on a CTA / FEA. Admin / CRD / Chairman or the request's
  // assigned Coordinator can flip this.
  toggleAssigneeViewerAccess: (id, assigneeUserId, viewerOnly) =>
    api.patch(`/requests/${id}/assignees/${assigneeUserId}/viewer-access`, { viewer_only: viewerOnly }),
  // FEA-CTA Linking
  getCompanyFeaRequests: () => api.get('/company-fea-requests'),
  validateFeaRef: (refNo) => api.get(`/validate-fea-ref/${encodeURIComponent(refNo)}`),
  // FT (Financial Transaction)
  // iter123 — undo a chairman/coordinator pre-emptive skip event
  undoSkippedApprovals: (id) => api.post(`/requests/${id}/undo-skipped-approvals`),
  getCoordinatorFtEligibleCtas: () => api.get('/coordinator/ft-eligible-ctas'),
  getCoordinatorFtStudyPicker: () => api.get('/coordinator/ft-study-picker'),
  getCoordinatorFtStudyContext: (studyId) => api.get(`/coordinator/ft-study-context/${studyId}`),
  getCoordinatorFtInvestigators: () => api.get('/coordinator/ft-investigators'),
  // FT workflow state machine
  ftEligibleFos: (id) => api.get(`/requests/${id}/ft-eligible-fos`),
  ftAssignFo: (id, foUserId) => api.post(`/requests/${id}/ft-assign-fo`, { fo_user_id: foUserId }),
  ftFoApprove: (id, notes) => api.post(`/requests/${id}/ft-fo-approve`, { notes: notes || null }),
  ftSkipFo: (id) => api.post(`/requests/${id}/ft-skip-fo`),
  getBundleTool: (id) => api.get(`/requests/${id}/bundle-tool`),
  generateBundle: (id, data) => api.post(`/requests/${id}/generate-bundle`, data),
  ftFoThreadAutoassign: (id) => api.post(`/requests/${id}/ft-fo-thread-autoassign`),
  coordinatorThreadAutoassign: (id) => api.post(`/requests/${id}/coordinator-thread-autoassign`),
  nudgeDocAssignees: (requestId, documentId) => api.post(`/requests/${requestId}/documents/${documentId}/nudge-assignees`),
  nudgeApprover: (requestId, assignmentId) => api.post(`/requests/${requestId}/nudge-approver`, { assignment_id: assignmentId }),
  getEmailLog: (requestId) => api.get(`/requests/${requestId}/email-log`),
  ftChairmanApprove: (id, payload) => api.post(`/requests/${id}/ft-chairman-approve`, payload),
  ftEsignEdit: (id, payload) => api.post(`/requests/${id}/ft-esign-edit`, payload),
  startSignatureRound: (id, payload) => api.post(`/requests/${id}/start-signature-round`, payload),
  // Review stages (Phase 5)
  reviewStageApprove: (id, notes) => api.post(`/requests/${id}/review-stage-approve`, { notes }),
  reviewStageReject: (id, reason) => api.post(`/requests/${id}/review-stage-reject`, { reason }),
  // iter142 — Chairman/Admin/CRD inline edit of review_stages + review_mode.
  updateReviewStages: (id, payload) => api.patch(`/requests/${id}/review-stages`, payload),
  ftFinanceApprove: (id, notes) => api.post(`/requests/${id}/ft-finance-approve`, { notes: notes || null }),
  ftDirectorApprove: (id, notes) => api.post(`/requests/${id}/ft-director-approve`, { notes: notes || null }),
  ftExecdirApprove: (id, notes) => api.post(`/requests/${id}/ft-execdir-approve`, { notes: notes || null }),
  ftReject: (id, reason) => api.post(`/requests/${id}/ft-reject`, { reason }),
  // Response Fields (FEA)
  getResponseFields: (id) => api.get(`/requests/${id}/response-fields`),
  updateResponseFields: (id, data) => api.put(`/requests/${id}/response-fields`, data),
};

// Handovers
export const handoversApi = {
  accept: (handoverId) => api.post(`/handovers/${handoverId}/accept`),
  reject: (handoverId, reason) => api.post(`/handovers/${handoverId}/reject?rejection_reason=${encodeURIComponent(reason || '')}`),
  getPendingForMe: () => api.get('/handovers/pending-for-me'),
  getEscalated: () => api.get('/handovers/escalated'),
  acknowledge: (handoverId) => api.post(`/handovers/${handoverId}/acknowledge`),
  oldCoordinatorAcknowledge: (handoverId) => api.post(`/handovers/${handoverId}/old-coordinator-acknowledge`),
};

// Coordinators
export const coordinatorsApi = {
  getAll: () => api.get('/coordinators'),
};

// Documents
export const documentsApi = {
  getAll: (requestId, params) => api.get(`/requests/${requestId}/documents`, { params }),
  getById: (documentId) => api.get(`/documents/${documentId}`),
  getDocTypes: (requestId) => api.get(`/requests/${requestId}/document-types`),
  // iter110: request-scoped list of existing custom bundle doc types
  listBundleDocTypes: (requestId) => api.get(`/requests/${requestId}/bundle-doc-types`),
  upload: (requestId, formData) => api.post(`/requests/${requestId}/documents`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  getDownloadUrl: (documentId) => api.get(`/documents/${documentId}/download-url`),
  download: (documentId) => api.get(`/documents/${documentId}/download-url`), // Alias for getDownloadUrl
  getPreviewUrl: (documentId) => api.get(`/documents/${documentId}/preview`),
  // Document status management
  updateStatus: (documentId, status, options = {}) => api.patch(`/documents/${documentId}/status`, { status, ...options }),
  updateStatusAssignees: (documentId, assigneeUserIds) => api.patch(`/documents/${documentId}/status-assignees`, { assignee_user_ids: assigneeUserIds }),
  getVersions: (documentId) => api.get(`/documents/${documentId}/versions`),
  // Thread attachment and version uploads
  uploadThreadAttachment: (threadId, formData) => api.post(`/threads/${threadId}/attachment`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  uploadNewVersionFromThread: (threadId, formData) => api.post(`/threads/${threadId}/new-version`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  }),
  // Attachment version relation
  updateAttachmentRelatedVersion: (documentId, relatedToVersion) => api.patch(`/attachments/${documentId}/related-version`, { related_to_version: relatedToVersion }),
  // Admin-only: hard delete a document + its discussion thread
  adminDelete: (requestId, documentId) => api.delete(`/requests/${requestId}/documents/${documentId}`),
};

// Assignments
export const assignmentsApi = {
  getAll: (requestId) => api.get(`/requests/${requestId}/assignments`),
  create: (requestId, data) => api.post(`/requests/${requestId}/assignments`, data),
  remove: (requestId, assignmentId) => api.delete(`/requests/${requestId}/assignments/${assignmentId}`),
  // iter121 — task summary for one assignee on this request
  getAssigneeTasks: (requestId, assigneeUserId) =>
    api.get(`/requests/${requestId}/assignees/${assigneeUserId}/tasks`),
  getEligiblePIs: (requestId) => api.get(`/requests/${requestId}/eligible-pis`),
  autoAssignPIs: (requestId) => api.post(`/requests/${requestId}/auto-assign-pis`),
  assignInvestigatorsBatch: (requestId, data) => api.post(`/requests/${requestId}/assign-investigators-batch`, data || {}),
  bulkAssignReviewers: (requestId, reviewerIds, viewerUserIds = null) =>
    api.post(`/requests/${requestId}/bulk-assign-reviewers`, {
      reviewer_ids: reviewerIds,
      // iter166c — Optional Viewer (read-only) IDs: when present, these
      // reviewer users will see all docs in the Documents tab read-only.
      ...(viewerUserIds && viewerUserIds.length ? { viewer_user_ids: viewerUserIds } : {}),
    }),
};

// Reviews (legacy - kept for backwards compatibility)
export const reviewsApi = {
  getAll: (requestId) => api.get(`/requests/${requestId}/reviews`),
  create: (requestId, data) => api.post(`/requests/${requestId}/reviews`, data),
};

// Approvals (new workflow)
export const approvalsApi = {
  getAll: (requestId) => api.get(`/requests/${requestId}/approvals`),
  submit: (requestId, action) => api.post(`/requests/${requestId}/approvals`, { action }),
  undo: (requestId, approvalId) => api.delete(`/requests/${requestId}/approvals/${approvalId}`),
  // Chairman/Admin/CRD pre-emptive skip flow for CTA
  getPendingApprovals: (requestId) => api.get(`/requests/${requestId}/pending-approvals`),
  skipPendingApprovals: (requestId) => api.post(`/requests/${requestId}/skip-pending-approvals`),
};

// Feasibility Responses
export const feasibilityApi = {
  getAll: (requestId) => api.get(`/requests/${requestId}/feasibility-responses`),
  submit: (requestId, data) => api.post(`/requests/${requestId}/feasibility-response`, data),
};

// Change Requests
// Change Requests
export const changeRequestsApi = {
  getAll: (requestId) => api.get(`/requests/${requestId}/change-requests`),
  create: (requestId, data) => api.post(`/requests/${requestId}/change-requests`, data),
};

// Audit Logs
export const auditLogsApi = {
  getAll: (requestId) => api.get(`/requests/${requestId}/audit-logs`),
};

// Comments
export const commentsApi = {
  getAll: (requestId, params) => api.get(`/requests/${requestId}/comments`, { params }),
  getByDocument: (documentId, params) => api.get(`/documents/${documentId}/comments`, { params }),
  create: (requestId, data) => api.post(`/requests/${requestId}/comments`, data),
  update: (commentId, data) => api.patch(`/comments/${commentId}`, data),
  delete: (commentId) => api.delete(`/comments/${commentId}`),
  getMentionUsers: (requestId, threadId = null) => {
    const params = threadId ? { thread_id: threadId } : {};
    return api.get(`/requests/${requestId}/mention-users`, { params });
  },
  markSeen: (commentId) => api.post(`/comments/${commentId}/mark-seen`),
  markSeenBulk: (commentIds) => api.post('/comments/mark-seen-bulk', commentIds),
  resolveThread: (commentId, resolved = true) => api.post(`/comments/${commentId}/resolve-thread?resolved=${resolved}`),
};

// Notifications
export const notificationsApi = {
  getAll: () => api.get('/notifications'),
  markRead: (id) => api.patch(`/notifications/${id}/read`),
  markAllRead: () => api.patch('/notifications/read-all'),
};

// Director & Executive Director
export const directorApi = {
  getPendingRequests: () => api.get('/director/pending-requests'),
  approve: (requestId) => api.post(`/requests/${requestId}/director-approve`),
  reject: (requestId) => api.post(`/requests/${requestId}/director-reject`),
};

export const execDirectorApi = {
  getPendingRequests: () => api.get('/exec-director/pending-requests'),
  approve: (requestId) => api.post(`/requests/${requestId}/exec-director-approve`),
  reject: (requestId) => api.post(`/requests/${requestId}/exec-director-reject`),
};

// CTA Final Approval with Document Selection
export const ctaFinalApprovalApi = {
  getSignableDocuments: (requestId) => api.get(`/requests/${requestId}/signable-documents`),
  getAvailableSignatories: (requestId) => api.get(`/requests/${requestId}/available-signatories`),
  getEligibleSignatoryUsers: (requestId) => api.get(`/requests/${requestId}/eligible-signatory-users`),
  getSignatureConfig: (requestId) => api.get(`/requests/${requestId}/signature-config`),
  submitFinalApproval: (requestId, documents, extra = {}) => api.post(`/requests/${requestId}/cta-final-approve`, { documents, ...extra }),
  submitFinalApprovalBundle: (requestId, payload) => api.post(`/requests/${requestId}/cta-final-approve`, { mode: 'bundle', ...payload }),
  editFinalApproval: (requestId, documents) => api.patch(`/requests/${requestId}/cta-final-approve-edit`, { documents }),
  executeRequest: (requestId) => api.post(`/requests/${requestId}/execute`),
  regenerateSignedPdf: (requestId, documentId) => api.post(`/requests/${requestId}/documents/${documentId}/regenerate-signed-pdf`),
};

// Signatures
export const signaturesApi = {
  getAll: (requestId) => api.get(`/requests/${requestId}/signatures`),
  getStatus: (requestId) => api.get(`/requests/${requestId}/signature-status`),
  getSigningPageData: (requestId) => api.get(`/requests/${requestId}/signing-page`),
  submitSignature: (requestId, typedName, documentId = null) => {
    const params = documentId ? `?document_id=${documentId}` : '';
    return api.post(`/requests/${requestId}/sign${params}`, { typed_name: typedName });
  },
  signMultipleDocuments: (requestId, documentIds, typedName) => {
    return api.post(`/requests/${requestId}/sign-multiple`, { 
      document_ids: documentIds, 
      typed_name: typedName 
    });
  },
  getMyPendingSignatures: () => api.get('/my-pending-signatures'),
  // iter167 Issue 11 — Chairman / Admin can edit / delete any individual
  // signature row on a running round.
  deleteSignature: (requestId, signatureId) =>
    api.delete(`/requests/${requestId}/signatures/${signatureId}`),
  editSignature: (requestId, signatureId, payload) =>
    api.patch(`/requests/${requestId}/signatures/${signatureId}`, payload),
};

// Dashboards
export const dashboardsApi = {
  getCRD: () => api.get('/dashboards/crd'),
  getAdmin: () => api.get('/dashboards/admin'),
  getInstitution: () => api.get('/dashboards/institution'),
  getConsultant: () => api.get('/dashboards/consultant'),
  getInvestigator: () => api.get('/dashboards/investigator'),
  getCoordinator: () => api.get('/dashboards/coordinator'),
  getReviewer: () => api.get('/dashboards/reviewer'),
  getDepartment: () => api.get('/dashboards/institution'), // alias for backward compat
  getSpecialtyDistribution: () => api.get('/dashboards/specialty-distribution'),
  getOverdueBreakdown: () => api.get('/dashboards/overdue-breakdown'),
  getOverdueDetails: () => api.get('/dashboards/overdue-details'),
  getMyOverdueDetails: () => api.get('/dashboards/my-overdue-details'),
  getMyPendingActions: () => api.get('/dashboards/my-pending-actions'),
  getAllPendingActions: () => api.get('/dashboards/all-pending-actions'),
  sendOverdueReminder: (assignmentId) => api.post(`/dashboards/overdue/${assignmentId}/send-reminder`),
  sendAllOverdueReminders: () => api.post('/dashboards/overdue/send-all-reminders'),
  getUserManagementStats: () => api.get('/dashboards/user-management-stats'),
};

// Admin
export const adminApi = {
  getUserSpecialties: () => api.get('/admin/user-specialties'),
  // iter148f — Admin reopen of an EXECUTED CTA: flips status back to
  // READY_TO_EXECUTE while keeping signatures intact.
  reopenExecuted: (requestId) => api.post(`/admin/requests/${requestId}/reopen-executed`),
  addUserSpecialty: (userId, specialtyId) => 
    api.post(`/admin/user-specialties?user_id=${userId}&specialty_id=${specialtyId}`),
  removeUserSpecialty: (userId, specialtyId) => 
    api.delete(`/admin/user-specialties?user_id=${userId}&specialty_id=${specialtyId}`),
  // Stage-based Reminder Policies
  getReminderStages: () => api.get('/admin/reminder-stages'),
  getReminderPolicies: () => api.get('/admin/reminder-policies'),
  getReminderPolicy: (stage) => api.get(`/admin/reminder-policies/${stage}`),
  updateReminderPolicy: (stage, data) => api.put(`/admin/reminder-policies/${stage}`, data),
  resetReminderPolicy: (stage) => api.delete(`/admin/reminder-policies/${stage}`),
  triggerReminders: () => api.post('/admin/trigger-reminders'),
  triggerStageReminder: (stage) => api.post(`/admin/trigger-reminders/${stage}`),
  getSchedulerStatus: () => api.get('/admin/scheduler-status'),
  // Email Templates
  getEmailTemplateCategories: () => api.get('/admin/email-template-categories'),
  getEmailTemplates: () => api.get('/admin/email-templates'),
  getEmailTemplate: (key) => api.get(`/admin/email-templates/${key}`),
  updateEmailTemplate: (key, data) => api.put(`/admin/email-templates/${key}`, data),
  updateEmailTemplateSettings: (key, data) => api.patch(`/admin/email-templates/${key}/settings`, data),
  resetEmailTemplate: (key) => api.delete(`/admin/email-templates/${key}`),
  previewEmailTemplate: (key, variables) => api.post(`/admin/email-templates/${key}/preview`, { variables }),
  testEmail: (data) => api.post('/admin/test-email', data),
  // Notification Configuration
  getNotificationConfig: (requestType) => api.get(`/admin/notification-config/${requestType}`),
  updateNotificationConfig: (requestType, notificationKey, data) => api.put(`/admin/notification-config/${requestType}/${notificationKey}`, data),
  bulkUpdateNotificationConfig: (requestType, data) => api.put(`/admin/notification-config/${requestType}/bulk`, data),
  testNotification: (requestType, notificationKey, data) => api.post(`/admin/notification-config/${requestType}/${notificationKey}/test`, data),
  resetNotificationConfig: (requestType) => api.delete(`/admin/notification-config/${requestType}/reset`),
  // Admin Full Control
  forceStatus: (requestId, data) => api.post(`/admin/requests/${requestId}/force-status`, data),
  getValidStatuses: (requestId) => api.get(`/admin/requests/${requestId}/valid-statuses`),
};

// Studies
export const studiesApi = {
  getAll: () => api.get('/studies'),
  get: (id) => api.get(`/studies/${id}`),
  create: (data) => api.post('/studies', data),
  update: (id, data) => api.patch(`/studies/${id}`, data),
  adminDelete: (id) => api.delete(`/admin/studies/${id}`),
};

// Profile
export const profileApi = {
  get: () => api.get('/profile'),
  update: (data) => api.patch('/profile', data),
  updateMe: (data) => api.patch('/profile', data),
  changePassword: (current_password, new_password) =>
    api.post('/profile/change-password', { current_password, new_password }),
  getExpiryAlerts: () => api.get('/profile/expiry-alerts'),
  uploadDocument: (formData) => api.post('/profile/documents', formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  deleteDocument: (docId) => api.delete(`/profile/documents/${docId}`),
  downloadDocument: (docId) => api.get(`/profile/documents/${docId}/download`, { responseType: 'blob' }),
  // Admin
  getUserProfile: (userId) => api.get(`/admin/users/${userId}/profile`),
  updateUserProfile: (userId, data) => api.patch(`/admin/users/${userId}/profile`, data),
  uploadUserDocument: (userId, formData) => api.post(`/admin/users/${userId}/documents`, formData, { headers: { 'Content-Type': 'multipart/form-data' } }),
  getRoleDocConfigs: () => api.get('/admin/role-document-configs'),
  getRoleDocConfigsByType: (requestType) => api.get(`/admin/role-document-configs/${requestType}`),
  updateRoleDocConfig: (role, data) => api.put(`/admin/role-document-configs/${role}`, data),
  getExpiryReport: () => api.get('/admin/users-expiry-report'),
  getRequestDocAlerts: (requestId) => api.get(`/requests/${requestId}/doc-alerts`),
  getAssigneeDocuments: (requestId, assigneeUserId) =>
    api.get(`/requests/${requestId}/assignees/${assigneeUserId}/documents`),
  sendUrgentDocEmail: (requestId, assigneeUserId, doc_type) =>
    api.post(`/requests/${requestId}/assignees/${assigneeUserId}/urgent-doc-email`, { doc_type }),
  downloadProfileDocument: (docId) => api.get(`/profile/documents/${docId}/download`, { responseType: 'blob' }),
  getDocNotifConfigs: (requestType) => api.get(`/admin/doc-notification-configs/${requestType}`),
  updateDocNotifConfigs: (requestType, data) => api.put(`/admin/doc-notification-configs/${requestType}`, data),
};

// Export
export const exportApi = {
  requests: () => api.get('/export/requests'),
  requestsCsv: (params = {}) => api.get('/export/requests/csv', { 
    params,
    responseType: 'blob' 
  }),
};

// Seed
export const seedApi = {
  seed: () => api.post('/seed'),
};

// IRB Resources (read-only library)
export const irbApi = {
  get: () => api.get('/irb-resources'),
  updateInfo: (info_text) => api.patch('/admin/irb-resources', { info_text }),
  uploadDoc: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/admin/irb-resources/documents', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  deleteDoc: (file_id) => api.delete(`/admin/irb-resources/documents/${file_id}`),
  updateDocDescription: (file_id, description) =>
    api.patch(`/admin/irb-resources/documents/${file_id}`, { description }),
  downloadUrl: (file_id) => `${API_URL}/template-files/${file_id}/download`,
};

// Admin Email Health
export const emailHealthApi = {
  status: () => api.get('/admin/email-health'),
  test: (to) => api.post('/admin/email-test', { to: to || null }),
  clear: () => api.post('/admin/email-failures/clear'),
};

// Admin Backup & Restore
export const backupApi = {
  list: () => api.get('/admin/backups'),
  create: () => api.post('/admin/backups', null, { timeout: 0 }),
  delete: (filename) => api.delete(`/admin/backups/${encodeURIComponent(filename)}`),
  download: (filename) => api.get(`/admin/backups/${encodeURIComponent(filename)}/download`, {
    responseType: 'blob',
    timeout: 0,
  }),
  restore: (file, password, onUploadProgress) => {
    const formData = new FormData();
    formData.append('backup_file', file);
    formData.append('password', password);
    formData.append('confirm_phrase', 'RESTORE');
    return api.post('/admin/backups/restore', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress,
      timeout: 0,
    });
  },
};

// ====== DM (iter163h) ======
export const dmApi = {
  listUsers: () => api.get('/dm/users'),
  listConversations: () => api.get('/dm/conversations'),
  startConversation: (userId) => api.post('/dm/conversations', { user_id: userId }),
  listMessages: (cid, params = {}) => api.get(`/dm/conversations/${cid}/messages`, { params }),
  sendMessage: (cid, payload) => api.post(`/dm/conversations/${cid}/messages`, payload),
  toggleReaction: (mid, emoji) => api.post(`/dm/messages/${mid}/reactions`, { emoji }),
  markRead: (mid) => api.post(`/dm/messages/${mid}/read`),
  typing: (cid) => api.post(`/dm/conversations/${cid}/typing`),
  unreadCount: () => api.get('/dm/unread'),
  getStatus: () => api.get('/dm/status'),
  setStatus: (message) => api.put('/dm/status', { message }),
  slashReq: (q) => api.get('/dm/slash/req', { params: { q } }),
  slashDoc: (q) => api.get('/dm/slash/doc', { params: { q } }),
};

export default api;
